import {
  ChangeDetectionStrategy,
  Component,
  input,
  model,
  output,
} from '@angular/core';
import type { ToggleSize } from './toggle.model';

@Component({
  selector: 'fc-toggle',
  standalone: true,
  host: {
    '[class.toggle-sm]': "size() === 'sm'",
  },
  template: `
    <div class="flex items-center gap-12">
      <label class="track relative inline-block shrink-0">
        <input
          #input
          type="checkbox"
          class="opacity-0 w-0 h-0"
          [checked]="checked()"
          [disabled]="disabled()"
          (change)="onToggle($event)"
        />
        <span
          class="slider"
          [class.cursor-pointer]="!disabled()"
          [class.cursor-not-allowed]="disabled()"
        ></span>
      </label>

      <div
        class="text-base select-none flex items-center"
        [class.cursor-pointer]="!disabled()"
        [class.cursor-not-allowed]="disabled()"
        [class.text-grey-900]="!disabled()"
        [class.text-grey-500]="disabled()"
        (click)="!disabled() && input.click()"
      >
        @if (label()) {
          <div class="flex flex-col gap-2">
            <span>{{ label() }}</span>
            @if (description()) {
              <span class="text-sm text-grey-500">{{ description() }}</span>
            }
          </div>
        } @else {
          <ng-content />
        }
      </div>
    </div>
  `,
  styles: `
    @layer toggle {
      :host {
        display: inline-block;
        --toggle-width: 39px;
        --toggle-height: 24px;
        --toggle-border-width: 1.5px;
        --toggle-knob-size: 18px;
        --toggle-knob-start: 3px;
        --toggle-knob-travel: 13.5px;
      }

      :host(.toggle-sm) {
        --toggle-width: 26px;
        --toggle-height: 16px;
        --toggle-border-width: 1px;
        --toggle-knob-size: 12px;
        --toggle-knob-start: 2px;
        --toggle-knob-travel: 9px;
      }

      .track {
        width: var(--toggle-width);
        height: var(--toggle-height);
      }

      .slider {
        position: absolute;
        inset: 0;
        background-color: var(--color-secondary-50);
        border: var(--toggle-border-width) solid var(--color-secondary-400);
        transition: 0.2s;
        border-radius: 75px;
      }

      .slider:before {
        position: absolute;
        content: '';
        inset-block: 0;
        left: var(--toggle-knob-start);
        margin-block: auto;
        height: var(--toggle-knob-size);
        width: var(--toggle-knob-size);
        background-color: var(--color-secondary-500);
        transition: 0.2s;
        border-radius: 50%;
      }

      input:checked + .slider {
        background-color: var(--color-primary-600);
        border-color: transparent;
      }

      input:checked + .slider:before {
        background-color: var(--color-white);
        transform: translateX(var(--toggle-knob-travel));
      }

      input:disabled + .slider {
        border-color: var(--color-secondary-100);
      }

      input:disabled + .slider:before {
        background-color: var(--color-secondary-100);
      }

      input:checked:disabled + .slider {
        background-color: var(--color-secondary-200);
        border-color: transparent;
      }

      input:checked:disabled + .slider:before {
        background-color: var(--color-white);
      }

      input:focus-visible + .slider {
        box-shadow: 0 0 0 2px var(--color-primary-100);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleComponent {
  readonly checked = model(false);
  readonly label = input<string>();
  readonly description = input<string>();
  readonly disabled = input(false);
  readonly size = input<ToggleSize>('md');

  readonly toggleChange = output<boolean>();

  onToggle(event: Event) {
    if (this.disabled()) {
      return;
    }
    const isChecked = (event.target as HTMLInputElement).checked;
    this.checked.set(isChecked);
    this.toggleChange.emit(isChecked);
  }
}
